import type { FlightMeta } from "../api/types";

export const DB_NAME = "vmcOffline";
export const DB_VERSION = 1;

/** Blob assets. Keys are `${flightId}:${kind}` for the single-instance kinds
 *  (video, preview, track, meta, thumbnail, annotations-baseline) and
 *  `${flightId}:${kind}:${id}` for the repeated ones — tile
 *  (`${basemap}:${z}:${x}:${y}`), pano, frame-full, frame-thumb, overlay
 *  (`<overlayId>`) and set-track (`<siblingFlightId>`).
 *  keyFromFingerprint in fingerprint.ts is the single source of truth.
 *
 *  Plan 13's overlay/set assets are keyed under the HOST flight (deliberate
 *  duplication rather than refcounting) so per-flight eviction and
 *  auditFlight keep working untouched. */
export const ASSETS_STORE = "vmcOfflineAssets";

/** Per-flight metadata — keyed by flightId. */
export const FLIGHTS_STORE = "vmcOfflineFlights";

export interface OfflineAssetRecord {
  key: string;            // e.g. "smoke-2018:video"
  flightId: string;
  kind: "video" | "preview" | "track" | "meta" | "thumbnail" | "annotations-baseline"
      | "tile" | "pano" | "frame-full" | "frame-thumb"
      | "overlay" | "overlays-manifest" | "set" | "set-track";
  blob: Blob;
  contentType: string;
  byteSize: number;
  storedAt: string;       // ISO
  /** For `:video` only — the quality id the blob was built from ("fmv",
   *  "preview", or a rung name). Absent on records written before 2026-08-27. */
  variant?: string;
}

export interface OfflineFlightRecord {
  flightId: string;
  team: string;
  cachedAt: string;
  videoQuality: string;
  byteSizeApprox: number;
  schemaVersion: number;  // bumped if annotations baseline format changes
  /** Set while a download is in flight and left behind when one is cancelled
   *  or fails, so /offline can show and remove the stranded assets. The final
   *  write of a completed download omits it. */
  partial?: true;
  /** Which basemap provider filled the `:tile:` keys (web/src/map/basemaps.ts
   *  OFFLINE_BASEMAP_PROVIDER). Tile keys are provider-agnostic, so this is
   *  what tells a refresh the cached map is from a retired provider. Absent
   *  on records written before 2026-09-09 (CARTO + keyless Esri tiles). */
  basemapProvider?: string;
  meta: FlightMeta;
  assetCounts: {
    tiles: number;
    panoramas: number;
    frames: number;
    /** 1 when preview.mp4 was stored under `:preview`; absent on older records. */
    preview?: 0 | 1;
    /** Team KMZ overlays cached under this flight (Plan 13); absent on older
     *  records. Counts the blobs actually stored, not the ones offered. */
    overlays?: number;
    /** Sibling set tracks cached under this flight (Plan 13); absent on older
     *  records. */
    setTracks?: number;
  };
}
